import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { usePermissions } from '../../hooks/usePermissions';
import {
  DEFAULT_SPRINT_STATUS,
  formatSprintDate,
  sprintStatusLabel,
} from '../../constants/agileSprints';
import { typeLabel } from '../../constants/agileTasks';
import TaskModal from './TaskModal';
import AiScrumMasterConfig from './AiScrumMasterConfig';
import {
  AI_ORCHESTRATION_DOC_ID,
  createDefaultAiOrchestrationSettings,
  normalizeAiOrchestrationSettings,
} from '../../lib/agileAiOrchestration';
import {
  DashboardModuleRoot,
  DashboardModuleToolbar,
  DashboardModuleBody,
} from '../layout/DashboardLayout';

const EMPTY_SPRINT_FORM = {
  name: '',
  goal: '',
  startDate: '',
  endDate: '',
};

function SprintCreateModal({ open, onClose, saving, onSubmit }) {
  const [form, setForm] = useState(EMPTY_SPRINT_FORM);

  useEffect(() => {
    if (open) setForm(EMPTY_SPRINT_FORM);
  }, [open]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.startDate || !form.endDate) return;
    onSubmit?.({
      name: form.name.trim(),
      goal: form.goal.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Create sprint</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            required
            autoFocus
            fullWidth
          />
          <TextField
            label="Goal"
            value={form.goal}
            onChange={(e) => setField('goal', e.target.value)}
            multiline
            minRows={2}
            fullWidth
            placeholder="What should this sprint achieve?"
          />
          <TextField
            label="Start date"
            type="date"
            value={form.startDate}
            onChange={(e) => setField('startDate', e.target.value)}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End date"
            type="date"
            value={form.endDate}
            onChange={(e) => setField('endDate', e.target.value)}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !form.name.trim() || !form.startDate || !form.endDate}
          >
            {saving ? 'Creating…' : 'Create sprint'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function BacklogTaskCard({
  task,
  plannedSprints,
  movingId,
  onAssignToSprint,
  onEdit,
  onDelete,
  canDelete,
  readOnly,
}) {
  return (
    <Paper
      elevation={1}
      draggable={!readOnly}
      onDragStart={readOnly ? undefined : (e) => {
        e.dataTransfer.setData('text/taskId', task.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: readOnly ? 'pointer' : 'grab',
        opacity: movingId === task.id ? 0.55 : 1,
        '&:hover': { boxShadow: readOnly ? 1 : 3 },
      }}
      onClick={() => onEdit(task)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">{task.title}</Typography>
          {task.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {task.description}
            </Typography>
          )}
        </Box>
        {canDelete && (
          <IconButton
            size="small"
            aria-label="Delete task"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(task.id);
            }}
            disabled={movingId === task.id}
            sx={{
              flexShrink: 0,
              mt: -0.5,
              mr: -0.5,
              color: 'text.secondary',
              '&:hover': { color: 'error.main' },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 1 }}>
        <Chip size="small" label={typeLabel(task.type)} variant="outlined" />
      </Box>
      {!readOnly && (
        <FormControl
          size="small"
          fullWidth
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'relative',
            zIndex: 50,
            isolation: 'isolate',
            mt: 0.5,
          }}
        >
          <InputLabel id={`assign-sprint-${task.id}`} shrink>
            Add to sprint
          </InputLabel>
          <Select
            labelId={`assign-sprint-${task.id}`}
            label="Add to sprint"
            value=""
            displayEmpty
            disabled={movingId === task.id || plannedSprints.length === 0}
            MenuProps={{
              disablePortal: false,
              PaperProps: {
                sx: { zIndex: 1600 },
              },
            }}
            onChange={(e) => {
              const sprintId = e.target.value;
              if (sprintId) onAssignToSprint(task.id, sprintId);
            }}
          >
            <MenuItem value="" disabled>
              <em>Select sprint…</em>
            </MenuItem>
            {plannedSprints.map((sprint) => (
              <MenuItem key={sprint.id} value={sprint.id}>
                {sprint.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Paper>
  );
}

function SprintColumn({
  sprint,
  tasks,
  movingId,
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onActivate,
  onRemoveTask,
  activating,
  readOnly,
}) {
  const isPlanned = sprint.status === 'planned' || !sprint.status;

  return (
    <Paper
      variant="outlined"
      onDragOver={readOnly ? undefined : onDragOver}
      onDragLeave={readOnly ? undefined : onDragLeave}
      onDrop={readOnly ? undefined : onDrop}
      sx={{
        p: 1.5,
        minHeight: 320,
        bgcolor: dragOver ? 'action.selected' : 'background.paper',
        borderStyle: dragOver ? 'dashed' : 'solid',
        borderColor: dragOver ? 'primary.main' : 'divider',
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {sprint.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatSprintDate(sprint.startDate)} – {formatSprintDate(sprint.endDate)}
          </Typography>
          {sprint.goal && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sprint.goal}
            </Typography>
          )}
        </Box>
        <Chip size="small" label={sprintStatusLabel(sprint)} color={sprint.status === 'active' ? 'success' : 'default'} />
      </Box>

      {isPlanned && !readOnly && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PlayArrowIcon />}
          onClick={() => onActivate(sprint.id)}
          disabled={activating}
          sx={{ mb: 1.5 }}
        >
          Set active
        </Button>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {tasks.length} task{tasks.length === 1 ? '' : 's'}
        {!readOnly && ' · Drop backlog items here'}
      </Typography>

      <Box sx={{ minHeight: 120 }}>
        {tasks.map((task) => (
          <Paper key={task.id} elevation={0} sx={{ p: 1, mb: 0.75, bgcolor: 'action.hover' }}>
            <Typography variant="body2" fontWeight={600}>
              {task.title}
            </Typography>
            {!readOnly && (
              <Button
                size="small"
                color="inherit"
                disabled={movingId === task.id}
                onClick={() => onRemoveTask(task.id)}
                sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: '0.75rem' }}
              >
                Remove to backlog
              </Button>
            )}
          </Paper>
        ))}
      </Box>
    </Paper>
  );
}

/**
 * Backlog & sprint planning — assign backlog tasks to upcoming sprints.
 * @param {{ userData: Record<string, unknown> | null }} props
 */
export default function SprintManager({ userData }) {
  const companyId = userData?.companyId;
  const uid = auth.currentUser?.uid;
  const { checkPermission } = usePermissions(userData);
  const canWriteAgile = checkPermission('agile', 'write');
  const canDeleteAgile = checkPermission('agile', 'delete');
  const canEditMad =
    userData?.role === 'company_admin' || userData?.role === 'super_admin';

  const [backlogTasks, setBacklogTasks] = useState([]);
  const [sprintTasks, setSprintTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const { users: companyUsers } = useCompanyUsers(companyId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [movingId, setMovingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [sprintModalOpen, setSprintModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragOverSprintId, setDragOverSprintId] = useState(null);
  const [aiOrchestration, setAiOrchestration] = useState(createDefaultAiOrchestrationSettings);
  const [savingOrchestration, setSavingOrchestration] = useState(false);

  useEffect(() => {
    if (!companyId) {
      setAiOrchestration(createDefaultAiOrchestrationSettings());
      return undefined;
    }

    const orchestrationRef = doc(
      db,
      'companies',
      companyId,
      'agile_settings',
      AI_ORCHESTRATION_DOC_ID,
    );

    const unsubOrchestration = onSnapshot(
      orchestrationRef,
      (snap) => {
        setAiOrchestration(
          normalizeAiOrchestrationSettings(snap.exists() ? snap.data() : null),
        );
      },
      (err) => {
        console.error('SprintManager ai orchestration:', err);
      },
    );

    return () => unsubOrchestration();
  }, [companyId]);

  const persistAiOrchestration = useCallback(
    async (nextSettings) => {
      if (!companyId || !uid || !canWriteAgile) return;
      setSavingOrchestration(true);
      setError('');
      try {
        await setDoc(
          doc(db, 'companies', companyId, 'agile_settings', AI_ORCHESTRATION_DOC_ID),
          {
            ...nextSettings,
            updatedAt: serverTimestamp(),
            updatedByUid: uid,
          },
          { merge: true },
        );
      } catch (e) {
        console.error('SprintManager save orchestration:', e);
        setError(e.message || 'Could not save AI orchestration settings.');
      } finally {
        setSavingOrchestration(false);
      }
    },
    [companyId, uid, canWriteAgile],
  );

  const handleToggleAgenticSwarm = async (enabled) => {
    const next = { ...aiOrchestration, enabled };
    setAiOrchestration(next);
    await persistAiOrchestration(next);
  };

  const handleSaveAiOrchestration = async () => {
    await persistAiOrchestration(aiOrchestration);
  };

  useEffect(() => {
    if (!companyId) {
      setBacklogTasks([]);
      setSprintTasks([]);
      setSprints([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError('');

    const backlogQuery = query(
      collection(db, 'tasks'),
      where('companyId', '==', companyId),
      where('sprintId', '==', null),
      orderBy('updatedAt', 'desc'),
    );

    const sprintedTasksQuery = query(
      collection(db, 'tasks'),
      where('companyId', '==', companyId),
      where('sprintId', '!=', null),
      orderBy('sprintId', 'asc'),
      orderBy('updatedAt', 'desc'),
    );

    const sprintsQuery = query(
      collection(db, 'sprints'),
      where('companyId', '==', companyId),
      orderBy('startDate', 'asc'),
    );

    const unsubBacklog = onSnapshot(
      backlogQuery,
      (snap) => {
        setBacklogTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('SprintManager backlog:', err);
        setError(err.message || 'Could not load backlog.');
        setLoading(false);
      },
    );

    const unsubSprintTasks = onSnapshot(
      sprintedTasksQuery,
      (snap) => {
        setSprintTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('SprintManager sprint tasks:', err);
        setError(err.message || 'Could not load sprint tasks.');
      },
    );

    const unsubSprints = onSnapshot(
      sprintsQuery,
      (snap) => {
        setSprints(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('SprintManager sprints:', err);
        setError(err.message || 'Could not load sprints.');
      },
    );

    return () => {
      unsubBacklog();
      unsubSprintTasks();
      unsubSprints();
    };
  }, [companyId]);

  const plannedSprints = useMemo(
    () => sprints.filter((s) => s.status === 'planned' || s.status === 'active' || !s.status),
    [sprints],
  );

  const upcomingSprints = useMemo(
    () =>
      plannedSprints
        .filter((s) => s.status === 'planned' || !s.status)
        .sort((a, b) => {
          const aStart = a.startDate?.toDate?.() ?? new Date(a.startDate || 0);
          const bStart = b.startDate?.toDate?.() ?? new Date(b.startDate || 0);
          return aStart - bStart;
        }),
    [plannedSprints],
  );

  const tasksBySprintId = useMemo(() => {
    const map = {};
    sprintTasks.forEach((task) => {
      if (!task.sprintId) return;
      if (!map[task.sprintId]) map[task.sprintId] = [];
      map[task.sprintId].push(task);
    });
    return map;
  }, [sprintTasks]);

  const assignTaskToSprint = useCallback(
    async (taskId, sprintId) => {
      if (!uid || !sprintId) return;
      setMovingId(taskId);
      setError('');
      try {
        await updateDoc(doc(db, 'tasks', taskId), {
          sprintId,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        });
      } catch (e) {
        console.error('SprintManager assign:', e);
        setError(e.message || 'Could not assign task to sprint.');
      } finally {
        setMovingId(null);
      }
    },
    [uid],
  );

  const removeTaskFromSprint = useCallback(
    async (taskId) => {
      if (!uid) return;
      setMovingId(taskId);
      setError('');
      try {
        await updateDoc(doc(db, 'tasks', taskId), {
          sprintId: null,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        });
      } catch (e) {
        console.error('SprintManager remove:', e);
        setError(e.message || 'Could not move task to backlog.');
      } finally {
        setMovingId(null);
      }
    },
    [uid],
  );

  const handleDeleteTask = useCallback(
    async (taskId) => {
      if (!uid || !companyId || !canDeleteAgile) return;
      if (!window.confirm('Are you sure you want to permanently delete this task? This action cannot be undone.')) {
        return;
      }

      const task = backlogTasks.find((t) => t.id === taskId);
      if (!task || task.companyId !== companyId) {
        setError('Cannot delete this task.');
        return;
      }

      setMovingId(taskId);
      setError('');
      try {
        await deleteDoc(doc(db, 'tasks', taskId));
      } catch (e) {
        console.error('SprintManager delete task:', e);
        setError(e.message || 'Could not delete task.');
      } finally {
        setMovingId(null);
      }
    },
    [uid, companyId, canDeleteAgile, backlogTasks],
  );

  const handleDropOnSprint = useCallback(
    (e, sprintId) => {
      e.preventDefault();
      setDragOverSprintId(null);
      const taskId = e.dataTransfer.getData('text/taskId');
      if (taskId && sprintId) assignTaskToSprint(taskId, sprintId);
    },
    [assignTaskToSprint],
  );

  const handleCreateSprint = async (payload) => {
    if (!companyId || !uid) return;
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'sprints'), {
        companyId,
        name: payload.name,
        goal: payload.goal || '',
        startDate: Timestamp.fromDate(new Date(`${payload.startDate}T00:00:00`)),
        endDate: Timestamp.fromDate(new Date(`${payload.endDate}T23:59:59`)),
        status: DEFAULT_SPRINT_STATUS,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: uid,
        updatedBy: uid,
      });
      setSprintModalOpen(false);
    } catch (e) {
      console.error('SprintManager create sprint:', e);
      setError(e.message || 'Could not create sprint.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivateSprint = async (sprintId) => {
    if (!uid) return;
    setActivatingId(sprintId);
    setError('');
    try {
      const currentlyActive = sprints.filter((s) => s.status === 'active' && s.id !== sprintId);
      await Promise.all(
        currentlyActive.map((s) =>
          updateDoc(doc(db, 'sprints', s.id), {
            status: 'completed',
            updatedAt: serverTimestamp(),
            updatedBy: uid,
          }),
        ),
      );
      await updateDoc(doc(db, 'sprints', sprintId), {
        status: 'active',
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      });
    } catch (e) {
      console.error('SprintManager activate:', e);
      setError(e.message || 'Could not activate sprint.');
    } finally {
      setActivatingId(null);
    }
  };

  const handleSaveTask = async (payload) => {
    if (!companyId || !uid) return;
    setSaving(true);
    setError('');
    try {
      if (editingTask?.id) {
        await updateDoc(doc(db, 'tasks', editingTask.id), {
          ...payload,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        });
      } else {
        await addDoc(collection(db, 'tasks'), {
          companyId,
          ...payload,
          projectId: null,
          sprintId: null,
          sortOrder: backlogTasks.length,
          linkedTicketIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
          updatedBy: uid,
        });
      }
      setTaskModalOpen(false);
      setEditingTask(null);
    } catch (e) {
      console.error('SprintManager save task:', e);
      setError(e.message || 'Could not save task.');
    } finally {
      setSaving(false);
    }
  };

  if (!companyId) {
    return (
      <Alert severity="info">
        You need a company assignment to manage sprints.
      </Alert>
    );
  }

  return (
    <DashboardModuleRoot>
      <DashboardModuleToolbar>
        <Typography variant="subtitle1" fontWeight={700}>
          Sprint planning
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(aiOrchestration.enabled)}
                onChange={(e) => handleToggleAgenticSwarm(e.target.checked)}
                disabled={!canWriteAgile || savingOrchestration}
              />
            }
            label={
              <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                Enable AI Agentic Swarm
              </Typography>
            }
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
          {canWriteAgile && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setEditingTask(null);
                  setTaskModalOpen(true);
                }}
              >
                New backlog task
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setSprintModalOpen(true)}
                sx={{ fontWeight: 700 }}
              >
                Create sprint
              </Button>
            </>
          )}
        </Box>
      </DashboardModuleToolbar>

      {aiOrchestration.enabled && (
        <AiScrumMasterConfig
          settings={aiOrchestration}
          onChange={setAiOrchestration}
          onSave={handleSaveAiOrchestration}
          saving={savingOrchestration}
          canEditMad={canEditMad}
          readOnly={!canWriteAgile}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mx: 1.5, mt: 1, flexShrink: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(280px, 1fr) minmax(360px, 1.2fr)' },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRight: { lg: 1 },
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 1,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Backlog
              </Typography>
              <Chip size="small" label={backlogTasks.length} />
            </Box>
            <DashboardModuleBody sx={{ px: 1.5, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Unscheduled tasks (no sprint assigned)
              </Typography>
              {backlogTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No backlog items. Create a task or move completed sprint work back here.
                </Typography>
              ) : (
                backlogTasks.map((task) => (
                  <BacklogTaskCard
                    key={task.id}
                    task={task}
                    plannedSprints={upcomingSprints}
                    movingId={movingId}
                    onAssignToSprint={assignTaskToSprint}
                    onEdit={(t) => {
                      setEditingTask(t);
                      setTaskModalOpen(true);
                    }}
                    onDelete={handleDeleteTask}
                    canDelete={canDeleteAgile}
                    readOnly={!canWriteAgile}
                  />
                ))
              )}
            </DashboardModuleBody>
          </Box>

          <DashboardModuleBody sx={{ px: 1.5, py: 1.5 }}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                pb: 1,
                mb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Upcoming sprints
              </Typography>
            </Box>
            {upcomingSprints.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No planned sprints yet. Create one to start scheduling backlog work.
                </Typography>
                {canWriteAgile && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setSprintModalOpen(true)}>
                    Create sprint
                  </Button>
                )}
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {upcomingSprints.map((sprint) => (
                  <SprintColumn
                    key={sprint.id}
                    sprint={sprint}
                    tasks={tasksBySprintId[sprint.id] || []}
                    movingId={movingId}
                    dragOver={dragOverSprintId === sprint.id}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverSprintId(sprint.id);
                    }}
                    onDragLeave={() => setDragOverSprintId(null)}
                    onDrop={(e) => handleDropOnSprint(e, sprint.id)}
                    onActivate={handleActivateSprint}
                    onRemoveTask={removeTaskFromSprint}
                    activating={activatingId === sprint.id}
                    readOnly={!canWriteAgile}
                  />
                ))}
              </Box>
            )}

            {sprints.some((s) => s.status === 'active') && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    py: 0.5,
                  }}
                >
                  Active sprint
                </Typography>
                {sprints
                  .filter((s) => s.status === 'active')
                  .map((sprint) => (
                    <SprintColumn
                      key={sprint.id}
                      sprint={sprint}
                      tasks={tasksBySprintId[sprint.id] || []}
                      movingId={movingId}
                      dragOver={dragOverSprintId === sprint.id}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOverSprintId(sprint.id);
                      }}
                      onDragLeave={() => setDragOverSprintId(null)}
                      onDrop={(e) => handleDropOnSprint(e, sprint.id)}
                      onActivate={handleActivateSprint}
                      onRemoveTask={removeTaskFromSprint}
                      activating={activatingId === sprint.id}
                      readOnly={!canWriteAgile}
                    />
                  ))}
              </Box>
            )}
          </DashboardModuleBody>
        </Box>
      )}

      <SprintCreateModal
        open={sprintModalOpen}
        onClose={() => { if (!saving) setSprintModalOpen(false); }}
        saving={saving}
        onSubmit={handleCreateSprint}
      />

      <TaskModal
        open={taskModalOpen}
        onClose={() => { if (!saving) { setTaskModalOpen(false); setEditingTask(null); } }}
        task={editingTask}
        companyId={companyId}
        userData={userData}
        companyUsers={companyUsers}
        saving={saving}
        onSave={handleSaveTask}
        readOnly={!canWriteAgile}
        aiOrchestration={aiOrchestration}
      />
    </DashboardModuleRoot>
  );
}
